import React, { useState } from 'react'
import { Button, Col, Row, Space, Table, Upload, message, Typography } from 'antd'
import { InboxOutlined, DownloadOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd/es/upload/interface'
import axios from 'axios'

const { Dragger } = Upload
const { Text, Title } = Typography

export interface CnCaFileInit {
  year: number
  fileId: string
  modelUrl: string
}

interface CnCaFileProps {
  orgaId: string
  initData: CnCaFileInit
  onUploaded: () => void
}

type UploadMode = 'replace' | 'upsert'

export function CnCaFile({ orgaId, initData, onUploaded }: CnCaFileProps) {
  const [replaceFileList, setReplaceFileList] = useState<UploadFile[]>([])
  const [upsertFileList, setUpsertFileList] = useState<UploadFile[]>([])
  const [uploadingMode, setUploadingMode] = useState<UploadMode | null>(null)

  const uploadFile = async (file: File, mode: UploadMode) => {
    const formData = new FormData()
    formData.append('file', file)

    const endpoint =
      mode === 'replace'
        ? `/organisations/${orgaId}/datacnegoce/cafile`
        : `/organisations/${orgaId}/datacnegoce/cafile/upsert`

    setUploadingMode(mode)
    try {
      await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      message.success(
        mode === 'replace'
          ? 'Fichier remplacé avec succès'
          : 'Lignes ajoutées / mises à jour avec succès'
      )
      if (mode === 'replace') setReplaceFileList([])
      else setUpsertFileList([])
      onUploaded()
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.error ||
        error?.response?.data ||
        'Erreur lors du traitement du fichier'
      message.error(typeof errMsg === 'string' ? errMsg : 'Erreur lors du traitement du fichier')
      if (mode === 'replace') setReplaceFileList([])
      else setUpsertFileList([])
    } finally {
      setUploadingMode(null)
    }
  }

  const busy = uploadingMode !== null

  const renderDragger = (
    mode: UploadMode,
    fileList: UploadFile[],
    setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>,
    title: string,
    description: string
  ) => (
    <div>
      <Title level={5} style={{ marginBottom: 4 }}>
        {title}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {description}
      </Text>
      <Dragger
        accept=".xlsx,.xls"
        maxCount={1}
        multiple={false}
        fileList={fileList}
        disabled={busy}
        beforeUpload={(file) => {
          setFileList([
            {
              uid: file.uid,
              name: file.name,
              status: 'uploading',
              originFileObj: file,
            },
          ])
          void uploadFile(file, mode)
          return false
        }}
        onRemove={() => {
          if (busy) return false
          setFileList([])
          return true
        }}
        showUploadList={{ showRemoveIcon: !busy }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {uploadingMode === mode
            ? 'Chargement en cours…'
            : 'Cliquez ou déposez un fichier ici'}
        </p>
      </Dragger>
    </div>
  )

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ justifySelf: 'end' }}>
          <Button href={initData.modelUrl} type="primary" icon={<DownloadOutlined />}>
            Fichier modèle
          </Button>
        </div>

        <Table
          pagination={false}
          bordered
          size="middle"
          dataSource={[{ key: initData.fileId, year: initData.year }]}
          columns={[
            {
              title: 'Année',
              dataIndex: 'year',
              key: 'year',
              render: (year: number) => (year ? year : <Text type="secondary">Non définie</Text>),
            },
          ]}
        />

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            {renderDragger(
              'replace',
              replaceFileList,
              setReplaceFileList,
              'Remplacer le fichier CA',
              'Remplace entièrement le fichier précédent : lignes absentes supprimées, statuts Client/Prospect recalculés.'
            )}
          </Col>
          <Col xs={24} lg={12}>
            {renderDragger(
              'upsert',
              upsertFileList,
              setUpsertFileList,
              'Ajouter un fichier',
              'Ajoute ou met à jour les lignes. Pas de suppression de lignes. Passage en statut Client pour les lignes.'

            )}
          </Col>
        </Row>
      </Space>
    </div>
  )
}
